import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { useTheme } from '../hooks/useTheme';
import { useI18n } from '../contexts/I18nContext';
import { exportAnnotationsJSON, createAnnotationImage, uploadAnnotationImage, updateAnnotationImage, deleteAnnotationImage, importImagesFromWeb } from '../utils/api';
import Slideshow, { annSlides, imgSrcSet } from './Slideshow';
import RichTextEditor from './RichTextEditor';
import { IconField } from './IconPicker';
import './AnnotationEditor.css';

function toPercent(val, max) { return (val / max) * 100; }
function fromPercent(val, max) { return (val / 100) * max; }
function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }

function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

const TOOL_ICONS = {
  select: '↖',
  rect: '▭',
  circle: '◯',
  point: '⊕',
  polygon: '⬡',
};

const COLORS = ['#5b5ef4','#e84393','#00d4aa','#ffb830','#ff4d6d','#a78bfa','#34d399','#fb923c'];
// Theme palette names a link button can use (mirrors annotateX-js LINK_COLORS).
// '' renders the default accent look. Labels are translated via `editor.color_<name>`.
const LINK_COLORS = ['primary', 'secondary', 'success', 'danger', 'warning', 'info'];
const ZOOM_STEP = 0.15;
const ZOOM_MIN = 0.2;
const ZOOM_MAX = 5;

// Dropdown for picking a link button's theme color. `value` is a LINK_COLORS
// name or '' (default). A small swatch previews the chosen palette colour.
function LinkColorSelect({ value, onChange, t, style }) {
  return (
    <select
      className="ann-img-input"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      title={t('editor.link_color')}
      style={{ borderLeft: value ? `4px solid var(--ax-link-${value}, var(--accent))` : undefined, ...style }}
    >
      <option value="">{t('editor.color_default')}</option>
      {LINK_COLORS.map(c => (
        <option key={c} value={c}>{t(`editor.color_${c}`)}</option>
      ))}
    </select>
  );
}

export default function AnnotationEditor({ image, annotations: initAnnotations, onSave, onClose, allTags = [], onSaveTags }) {
  const { t } = useI18n();
  const wrapRef = useRef(null);       // scrollable container
  const canvasRef = useRef(null);     // the zoomed inner div
  const svgRef = useRef(null);
  const imgRef = useRef(null);

  const [annotations, setAnnotations] = useState(initAnnotations || []);
  const [tool, setTool] = useState('select');
  const [drawing, setDrawing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [pendingAnnotation, setPendingAnnotation] = useState(null);
  const [editingMeta, setEditingMeta] = useState(null); // {ann} for code/label dialog
  const [renderSize, setRenderSize] = useState({ w: 0, h: 0 });
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [colorIdx, setColorIdx] = useState(0);
  const [saved, setSaved] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [zoomReady, setZoomReady] = useState(false);
  const [imageTags, setImageTags] = useState(() => (image.tags || []).map(t => t.id));
  const [polyMode, setPolyMode] = useState(null); // null | 'add' | 'remove'
  const [hoveredVertex, setHoveredVertex] = useState(null); // { annId, idx }
  const [hoveredShape, setHoveredShape] = useState(null);  // ann.id hovered on canvas
  const [showCoords, setShowCoords] = useState(false);
  const annListRef = useRef(null);
  const [, toggleTheme] = useTheme();

  // TYPE_LABELS uses t() — defined here inside component
  const TYPE_LABELS = {
    rect: t('type.rect'),
    circle: t('type.circle'),
    point: t('type.point'),
    polygon: t('type.polygon'),
  };

  // Context menu for polygon right-click
  const [ctxMenu, setCtxMenu] = useState(null);
  // { screenX, screenY, type: 'edge'|'vertex', annId, edgeIdx, vertexIdx, insertPx, insertPy }

  // Dragging a handle on selected annotation
  const [handleDrag, setHandleDrag] = useState(null);
  // {annId, handle: 'tl'|'tr'|'bl'|'br'|'c'|'rx'|'ry'|'cx'|'cy'|point_idx|'move', startX, startY, origData}

  // Middle-mouse panning
  const panRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);

  // Tooltip show/hide timers
  const ttShowTimer = useRef(null);
  const ttHideTimer = useRef(null);
  const ttPosRef    = useRef({ x: 0, y: 0 }); // tracks mouse pos while waiting to show

  // Annotation history for undo/redo
  const historyRef = useRef({ past: [], future: [] });
  const preDragRef = useRef(null);   // snapshot before a drag starts
  const dragMovedRef = useRef(false); // whether the drag actually moved

  // Always-current refs — let keyboard handler read latest state without stale closures
  const toolRef = useRef(tool);
  toolRef.current = tool;
  const polygonPointsRef = useRef(polygonPoints);
  polygonPointsRef.current = polygonPoints;

  // Scroll hovered annotation list item into view
  useEffect(() => {
    if (!hoveredShape || !annListRef.current) return;
    const el = annListRef.current.querySelector(`[data-ann-id="${hoveredShape}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [hoveredShape]);

  // --- Auto-zoom on mount so image fits without scrollbar ---
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || zoomReady) return;
    const iw = image.original_width;
    const ih = image.original_height;
    if (!iw || !ih) { setZoomReady(true); return; } // no dimensions — default zoom

    const tryFit = () => {
      const ww = wrap.clientWidth - 40;
      const wh = wrap.clientHeight - 40;
      if (ww <= 0 || wh <= 0) return false; // layout not ready yet
      const fit = Math.min(ww / iw, wh / ih, 1);
      setZoom(clamp(parseFloat(fit.toFixed(3)), ZOOM_MIN, ZOOM_MAX));
      setZoomReady(true);
      return true;
    };

    if (tryFit()) return;

    // Container has no size yet — retry via ResizeObserver when it gets dimensions
    const obs = new ResizeObserver(() => { if (tryFit()) obs.disconnect(); });
    obs.observe(wrap);
    return () => obs.disconnect();
  }, [image, zoomReady]);

  // The SVG coordinate space is always the original image dimensions, regardless of zoom.
  useEffect(() => {
    setRenderSize({ w: image.original_width, h: image.original_height });
  }, [image]);

  // Scroll to zoom; middle-mouse drag to pan
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const onWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      setZoom(z => clamp(parseFloat((z + delta).toFixed(3)), ZOOM_MIN, ZOOM_MAX));
    };

    const onMouseDown = (e) => {
      if (e.button !== 1) return;
      e.preventDefault(); // suppress browser auto-scroll mode
      panRef.current = { x: e.clientX, y: e.clientY, sl: wrap.scrollLeft, st: wrap.scrollTop };
      setIsPanning(true);
    };
    const onMouseMove = (e) => {
      if (!panRef.current) return;
      wrap.scrollLeft = panRef.current.sl - (e.clientX - panRef.current.x);
      wrap.scrollTop  = panRef.current.st - (e.clientY - panRef.current.y);
    };
    const onMouseUp = (e) => {
      if (e.button !== 1 || !panRef.current) return;
      panRef.current = null;
      setIsPanning(false);
    };

    wrap.addEventListener('wheel', onWheel, { passive: false });
    wrap.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      wrap.removeEventListener('wheel', onWheel);
      wrap.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const getSVGCoords = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return { x: (e.clientX - rect.left) / zoom, y: (e.clientY - rect.top) / zoom };
  }, [zoom]);

  const pxToPercent = useCallback((x, y) => ({
    px: toPercent(x, renderSize.w),
    py: toPercent(y, renderSize.h),
  }), [renderSize]);

  // History helpers — declared early so all handlers below can include them in deps
  const pushHistory = useCallback((snapshot) => {
    historyRef.current.past.push(JSON.parse(JSON.stringify(snapshot)));
    if (historyRef.current.past.length > 50) historyRef.current.past.shift();
    historyRef.current.future = [];
  }, []);

  const undo = useCallback(() => {
    const { past, future } = historyRef.current;
    if (past.length === 0) return;
    setAnnotations(current => { future.push(JSON.parse(JSON.stringify(current))); return past.pop(); });
  }, []);

  const redo = useCallback(() => {
    const { past, future } = historyRef.current;
    if (future.length === 0) return;
    setAnnotations(current => { past.push(JSON.parse(JSON.stringify(current))); return future.pop(); });
  }, []);

  // Keyboard shortcuts: Ctrl+Z/Y undo/redo, Ctrl+Plus/Minus zoom
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName.toLowerCase();
      const isInput = tag === 'input' || tag === 'textarea' || tag === 'select' || document.activeElement?.contentEditable === 'true';
      if (e.ctrlKey || e.metaKey) {
        if ((e.key === 'z' || e.key === 'Z') && !isInput) {
          e.preventDefault();
          if (e.shiftKey) {
            redo();
          } else if (toolRef.current === 'polygon' && polygonPointsRef.current.length > 0) {
            setPolygonPoints(pts => pts.slice(0, -1));
          } else {
            undo();
          }
          return;
        }
        if ((e.key === 'y' || e.key === 'Y') && !isInput) {
          e.preventDefault(); redo(); return;
        }
        if (e.key === '+' || e.key === '=' || e.key === 'Equal') {
          e.preventDefault();
          setZoom(z => clamp(parseFloat((z + ZOOM_STEP).toFixed(3)), ZOOM_MIN, ZOOM_MAX));
          return;
        }
        if (e.key === '-' || e.key === 'Minus') {
          e.preventDefault();
          setZoom(z => clamp(parseFloat((z - ZOOM_STEP).toFixed(3)), ZOOM_MIN, ZOOM_MAX));
          return;
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);

  // ---- Drawing new shapes ----
  const handleMouseDown = useCallback((e) => {
    if (tool === 'select' || tool === 'polygon') return;
    if (tool === 'point') return;
    if (e.button !== 0) return;
    e.preventDefault();
    const { x, y } = getSVGCoords(e);
    setDrawing({ startX: x, startY: y, currentX: x, currentY: y });
  }, [tool, getSVGCoords]);

  const handleMouseMove = useCallback((e) => {
    // Handle drag of annotation handle
    if (handleDrag) {
      e.preventDefault();
      dragMovedRef.current = true;
      const { x, y } = getSVGCoords(e);
      const dx = x - handleDrag.startX;
      const dy = y - handleDrag.startY;
      const dxp = toPercent(dx, renderSize.w);
      const dyp = toPercent(dy, renderSize.h);
      const od = handleDrag.origData;

      setAnnotations(prev => prev.map(a => {
        if (a.id !== handleDrag.annId) return a;
        let nd = { ...a.data };
        const h = handleDrag.handle;

        if (a.type === 'rect') {
          if (h === 'move') {
            nd = { ...od, x: od.x + dxp, y: od.y + dyp };
          } else if (h === 'tl') {
            const newX = od.x + dxp; const newY = od.y + dyp;
            nd = { x: newX, y: newY, w: od.w - dxp, h: od.h - dyp };
          } else if (h === 'tr') {
            nd = { x: od.x, y: od.y + dyp, w: od.w + dxp, h: od.h - dyp };
          } else if (h === 'bl') {
            nd = { x: od.x + dxp, y: od.y, w: od.w - dxp, h: od.h + dyp };
          } else if (h === 'br') {
            nd = { x: od.x, y: od.y, w: od.w + dxp, h: od.h + dyp };
          } else if (h === 'tc') {
            nd = { x: od.x, y: od.y + dyp, w: od.w, h: od.h - dyp };
          } else if (h === 'bc') {
            nd = { x: od.x, y: od.y, w: od.w, h: od.h + dyp };
          } else if (h === 'lc') {
            nd = { x: od.x + dxp, y: od.y, w: od.w - dxp, h: od.h };
          } else if (h === 'rc') {
            nd = { x: od.x, y: od.y, w: od.w + dxp, h: od.h };
          }
          // ensure positive w/h
          if (nd.w < 0.5) nd.w = 0.5;
          if (nd.h < 0.5) nd.h = 0.5;
        } else if (a.type === 'circle') {
          if (h === 'move') {
            nd = { ...od, cx: od.cx + dxp, cy: od.cy + dyp };
          } else if (h === 'rx') {
            nd = { ...od, rx: Math.max(0.5, od.rx + dxp) };
          } else if (h === 'ry') {
            nd = { ...od, ry: Math.max(0.5, od.ry + dyp) };
          } else if (h === 'rx-') {
            nd = { ...od, rx: Math.max(0.5, od.rx - dxp) };
          } else if (h === 'ry-') {
            nd = { ...od, ry: Math.max(0.5, od.ry - dyp) };
          }
        } else if (a.type === 'point') {
          nd = { cx: od.cx + dxp, cy: od.cy + dyp };
        } else if (a.type === 'polygon') {
          if (h === 'move') {
            nd = { points: od.points.map(p => ({ px: p.px + dxp, py: p.py + dyp })) };
          } else if (typeof h === 'number') {
            nd = {
              points: od.points.map((p, i) =>
                i === h ? { px: od.points[i].px + dxp, py: od.points[i].py + dyp } : p
              )
            };
          }
        }
        return { ...a, data: nd };
      }));
      return;
    }
    if (!drawing) return;
    const { x, y } = getSVGCoords(e);
    setDrawing(d => ({ ...d, currentX: x, currentY: y }));
  }, [handleDrag, drawing, getSVGCoords, renderSize]);

  const handleMouseUp = useCallback((e) => {
    if (handleDrag) {
      if (dragMovedRef.current && preDragRef.current !== null) {
        pushHistory(preDragRef.current);
      }
      preDragRef.current = null;
      dragMovedRef.current = false;
      setHandleDrag(null);
      return;
    }
    if (!drawing || tool === 'select') return;
    const { x, y } = getSVGCoords(e);
    const dx = Math.abs(x - drawing.startX);
    const dy = Math.abs(y - drawing.startY);
    if (dx < 3 && dy < 3) { setDrawing(null); return; }

    const { px: x1p, py: y1p } = pxToPercent(drawing.startX, drawing.startY);
    const { px: x2p, py: y2p } = pxToPercent(x, y);

    let data = {};
    if (tool === 'rect') {
      data = { x: Math.min(x1p, x2p), y: Math.min(y1p, y2p), w: Math.abs(x2p - x1p), h: Math.abs(y2p - y1p) };
    } else if (tool === 'circle') {
      data = { cx: (x1p + x2p) / 2, cy: (y1p + y2p) / 2, rx: Math.abs(x2p - x1p) / 2, ry: Math.abs(y2p - y1p) / 2 };
    }
    setPendingAnnotation({ type: tool, data, color: COLORS[colorIdx % COLORS.length] });
    setDrawing(null);
  }, [handleDrag, drawing, tool, getSVGCoords, pxToPercent, colorIdx, pushHistory]);

  const handleSVGClick = useCallback((e) => {
    const curSel = annotations.find(a => a.id === selected);
    if (polyMode === 'add' && curSel?.type === 'polygon') {
      e.preventDefault();
      const { x, y } = getSVGCoords(e);
      const pts = curSel.data.points;
      let minDist = Infinity;
      let insertIdx = 0;
      for (let i = 0; i < pts.length; i++) {
        const next = (i + 1) % pts.length;
        const d = distToSegment(x, y,
          fromPercent(pts[i].px, renderSize.w), fromPercent(pts[i].py, renderSize.h),
          fromPercent(pts[next].px, renderSize.w), fromPercent(pts[next].py, renderSize.h));
        if (d < minDist) { minDist = d; insertIdx = i; }
      }
      const newPt = { px: toPercent(x, renderSize.w), py: toPercent(y, renderSize.h) };
      pushHistory(annotations);
      setAnnotations(prev => prev.map(a => {
        if (a.id !== curSel.id) return a;
        const p = [...a.data.points];
        p.splice(insertIdx + 1, 0, newPt);
        return { ...a, data: { points: p } };
      }));
      return;
    }
    if (tool !== 'polygon' && tool !== 'point') return;
    e.preventDefault();
    const { x, y } = getSVGCoords(e);
    const { px, py } = pxToPercent(x, y);
    if (tool === 'point') {
      setPendingAnnotation({ type: 'point', data: { cx: px, cy: py }, color: COLORS[colorIdx % COLORS.length] });
      return;
    }
    if (tool === 'polygon') {
      setPolygonPoints(pts => [...pts, { px, py }]);
    }
  }, [polyMode, selected, annotations, tool, getSVGCoords, pxToPercent, colorIdx, renderSize, pushHistory]);

  const finishPolygon = useCallback(() => {
    if (polygonPoints.length < 3) return;
    setPendingAnnotation({ type: 'polygon', data: { points: polygonPoints }, color: COLORS[colorIdx % COLORS.length] });
    setPolygonPoints([]);
  }, [polygonPoints, colorIdx]);

  const confirmAnnotation = useCallback((code, title, content, link, _images, linkColor, linkIcon) => {
    if (!pendingAnnotation) return;
    pushHistory(annotations);
    const ann = { id: uuidv4(), code, title, content: content || '', link: link || null, linkColor: linkColor || null, linkIcon: linkIcon || null, type: pendingAnnotation.type, data: pendingAnnotation.data, color: pendingAnnotation.color };
    setAnnotations(a => [...a, ann]);
    setPendingAnnotation(null);
    setColorIdx(i => i + 1);
  }, [pendingAnnotation, pushHistory, annotations]);

  const deleteAnnotation = useCallback((id) => {
    pushHistory(annotations);
    setAnnotations(a => a.filter(x => x.id !== id));
    if (selected === id) setSelected(null);
  }, [selected, pushHistory, annotations]);

  // Clear selection when the selected annotation no longer exists (e.g. undo of creation)
  useEffect(() => {
    if (selected && !annotations.find(a => a.id === selected)) {
      setSelected(null);
      setPolyMode(null);
    }
  }, [annotations, selected]);

  // Close context menu on outside click
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    return () => { window.removeEventListener('click', close); window.removeEventListener('contextmenu', close); };
  }, [ctxMenu]);

  const insertPolygonPoint = useCallback((annId, edgeIdx, px, py) => {
    pushHistory(annotations);
    setAnnotations(prev => prev.map(a => {
      if (a.id !== annId) return a;
      const pts = [...a.data.points];
      pts.splice(edgeIdx + 1, 0, { px, py });
      return { ...a, data: { points: pts } };
    }));
    setCtxMenu(null);
  }, [pushHistory, annotations]);

  const deletePolygonPoint = useCallback((annId, vertexIdx) => {
    pushHistory(annotations);
    setAnnotations(prev => prev.map(a => {
      if (a.id !== annId) return a;
      const pts = a.data.points.filter((_, i) => i !== vertexIdx);
      if (pts.length < 3) return a; // keep minimum 3 points
      return { ...a, data: { points: pts } };
    }));
    setCtxMenu(null);
  }, [pushHistory, annotations]);

  const handleSave = useCallback(async () => {
    await onSave(annotations);
    if (onSaveTags) await onSaveTags(imageTags);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [annotations, imageTags, onSave, onSaveTags]);

  // ---- Resize handles ----
  const HANDLE_R = 5;

  const startHandleDrag = (e, annId, handle, origData) => {
    e.stopPropagation(); e.preventDefault();
    const { x, y } = getSVGCoords(e);
    preDragRef.current = annotations;
    dragMovedRef.current = false;
    setHandleDrag({ annId, handle, startX: x, startY: y, origData: JSON.parse(JSON.stringify(origData)) });
  };

  const renderHandles = (ann) => {
    const color = ann.color || COLORS[0];
    const hr = HANDLE_R;
    const handleStyle = { cursor: 'move', fill: '#fff', stroke: color, strokeWidth: 1.5 };

    if (ann.type === 'rect') {
      const x = fromPercent(ann.data.x, renderSize.w);
      const y = fromPercent(ann.data.y, renderSize.h);
      const w = fromPercent(ann.data.w, renderSize.w);
      const h = fromPercent(ann.data.h, renderSize.h);
      const corners = [
        { id: 'tl', cx: x, cy: y }, { id: 'tr', cx: x + w, cy: y },
        { id: 'bl', cx: x, cy: y + h }, { id: 'br', cx: x + w, cy: y + h },
        { id: 'tc', cx: x + w/2, cy: y }, { id: 'bc', cx: x + w/2, cy: y + h },
        { id: 'lc', cx: x, cy: y + h/2 }, { id: 'rc', cx: x + w, cy: y + h/2 },
      ];
      const cursorMap = { tl:'nw-resize', tr:'ne-resize', bl:'sw-resize', br:'se-resize', tc:'n-resize', bc:'s-resize', lc:'w-resize', rc:'e-resize' };
      return (
        <g>
          {/* Move area */}
          <rect x={x} y={y} width={w} height={h} fill="transparent"
            style={{ cursor: 'move' }}
            onMouseDown={e => startHandleDrag(e, ann.id, 'move', ann.data)} />
          {corners.map(c => (
            <circle key={c.id} cx={c.cx} cy={c.cy} r={hr}
              style={{ ...handleStyle, cursor: cursorMap[c.id] }}
              onMouseDown={e => startHandleDrag(e, ann.id, c.id, ann.data)} />
          ))}
        </g>
      );
    }
    if (ann.type === 'circle') {
      const cx = fromPercent(ann.data.cx, renderSize.w);
      const cy = fromPercent(ann.data.cy, renderSize.h);
      const rx = fromPercent(ann.data.rx, renderSize.w);
      const ry = fromPercent(ann.data.ry, renderSize.h);
      return (
        <g>
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="transparent" style={{ cursor: 'move' }}
            onMouseDown={e => startHandleDrag(e, ann.id, 'move', ann.data)} />
          <circle cx={cx + rx} cy={cy} r={hr} style={{ ...handleStyle, cursor: 'e-resize' }}
            onMouseDown={e => startHandleDrag(e, ann.id, 'rx', ann.data)} />
          <circle cx={cx - rx} cy={cy} r={hr} style={{ ...handleStyle, cursor: 'w-resize' }}
            onMouseDown={e => startHandleDrag(e, ann.id, 'rx-', ann.data)} />
          <circle cx={cx} cy={cy + ry} r={hr} style={{ ...handleStyle, cursor: 's-resize' }}
            onMouseDown={e => startHandleDrag(e, ann.id, 'ry', ann.data)} />
          <circle cx={cx} cy={cy - ry} r={hr} style={{ ...handleStyle, cursor: 'n-resize' }}
            onMouseDown={e => startHandleDrag(e, ann.id, 'ry-', ann.data)} />
          <circle cx={cx} cy={cy} r={hr} style={{ ...handleStyle, cursor: 'move', fill: color }}
            onMouseDown={e => startHandleDrag(e, ann.id, 'move', ann.data)} />
        </g>
      );
    }
    if (ann.type === 'point') {
      const cx = fromPercent(ann.data.cx, renderSize.w);
      const cy = fromPercent(ann.data.cy, renderSize.h);
      return (
        <circle cx={cx} cy={cy} r={hr + 4} fill="transparent" style={{ cursor: 'move' }}
          onMouseDown={e => startHandleDrag(e, ann.id, 'move', ann.data)} />
      );
    }
    if (ann.type === 'polygon') {
      const pts = ann.data.points;

      if (polyMode === 'remove') {
        return (
          <g>
            {pts.map((p, i) => (
              <circle key={i}
                cx={fromPercent(p.px, renderSize.w)} cy={fromPercent(p.py, renderSize.h)}
                r={hr + 4}
                style={{ fill: 'rgba(255,77,109,0.85)', stroke: '#fff', strokeWidth: 1.5, cursor: 'pointer' }}
                onClick={e => { e.stopPropagation(); deletePolygonPoint(ann.id, i); }}
              />
            ))}
          </g>
        );
      }

      return (
        <g>
          {/* Edge hit areas with right-click to insert point */}
          {pts.map((p, i) => {
            const next = pts[(i + 1) % pts.length];
            const x1 = fromPercent(p.px, renderSize.w);
            const y1 = fromPercent(p.py, renderSize.h);
            const x2 = fromPercent(next.px, renderSize.w);
            const y2 = fromPercent(next.py, renderSize.h);
            return (
              <line key={`edge-${i}`} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="transparent" strokeWidth={12} style={{ cursor: polyMode === 'add' ? 'crosshair' : 'default' }}
                onContextMenu={polyMode ? undefined : e => {
                  e.preventDefault(); e.stopPropagation();
                  const { x, y } = getSVGCoords(e);
                  setCtxMenu({
                    screenX: e.clientX, screenY: e.clientY,
                    type: 'edge', annId: ann.id, edgeIdx: i,
                    insertPx: toPercent(x, renderSize.w),
                    insertPy: toPercent(y, renderSize.h),
                  });
                }}
              />
            );
          })}
          {/* Move - click on polygon body; double-click adds point at nearest edge */}
          <polygon
            points={pts.map(p => `${fromPercent(p.px, renderSize.w)},${fromPercent(p.py, renderSize.h)}`).join(' ')}
            fill="transparent"
            style={{ cursor: polyMode === 'add' ? 'crosshair' : 'move' }}
            onMouseDown={polyMode ? undefined : e => startHandleDrag(e, ann.id, 'move', ann.data)}
            onDoubleClick={polyMode ? undefined : e => {
              e.stopPropagation(); e.preventDefault();
              const { x, y } = getSVGCoords(e);
              let minDist = Infinity, insertIdx = 0;
              for (let j = 0; j < pts.length; j++) {
                const nxt = (j + 1) % pts.length;
                const d = distToSegment(x, y,
                  fromPercent(pts[j].px, renderSize.w), fromPercent(pts[j].py, renderSize.h),
                  fromPercent(pts[nxt].px, renderSize.w), fromPercent(pts[nxt].py, renderSize.h));
                if (d < minDist) { minDist = d; insertIdx = j; }
              }
              insertPolygonPoint(ann.id, insertIdx, toPercent(x, renderSize.w), toPercent(y, renderSize.h));
            }}
          />
          {/* Vertex handles — hover highlights, double-click removes point */}
          {pts.map((p, i) => {
            const isHov = hoveredVertex?.annId === ann.id && hoveredVertex?.idx === i;
            return (
              <circle key={i}
                cx={fromPercent(p.px, renderSize.w)} cy={fromPercent(p.py, renderSize.h)}
                r={isHov ? hr + 3 : hr}
                style={{
                  fill: isHov ? color : '#fff',
                  stroke: color,
                  strokeWidth: isHov ? 2 : 1.5,
                  cursor: isHov ? 'pointer' : 'grab',
                }}
                onMouseEnter={() => setHoveredVertex({ annId: ann.id, idx: i })}
                onMouseLeave={() => setHoveredVertex(null)}
                onMouseDown={polyMode ? undefined : e => startHandleDrag(e, ann.id, i, ann.data)}
                onDoubleClick={polyMode ? undefined : e => {
                  e.stopPropagation(); e.preventDefault();
                  deletePolygonPoint(ann.id, i);
                }}
                onContextMenu={polyMode ? undefined : e => {
                  e.preventDefault(); e.stopPropagation();
                  setCtxMenu({
                    screenX: e.clientX, screenY: e.clientY,
                    type: 'vertex', annId: ann.id, vertexIdx: i,
                  });
                }}
              />
            );
          })}
        </g>
      );
    }
    return null;
  };

  // ---- Render annotation ----
  const renderAnnotation = (ann, isSelected) => {
    const color = ann.color || COLORS[0];
    const strokeW = isSelected ? 2.5 : 1.8;
    const opacity = isSelected ? 0.32 : 0.18;

    const onEnter = (e) => {
      if (handleDrag) return;
      clearTimeout(ttHideTimer.current);
      clearTimeout(ttShowTimer.current);
      ttPosRef.current = { x: e.clientX, y: e.clientY };
      ttShowTimer.current = setTimeout(() => {
        setTooltip({ id: ann.id, screenX: ttPosRef.current.x, screenY: ttPosRef.current.y });
      }, 400);
      if (!editingMeta) setHoveredShape(ann.id);
    };
    const onMove = (e) => {
      if (handleDrag) return;
      ttPosRef.current = { x: e.clientX, y: e.clientY };
    };
    const onLeave = () => {
      clearTimeout(ttShowTimer.current);
      ttHideTimer.current = setTimeout(() => setTooltip(null), 200);
      setHoveredShape(null);
    };
    const onClick = (e) => {
      if (polyMode === 'add' && ann.id === selected) return; // propagate to SVG handler
      e.stopPropagation();
      if (tool === 'select') setSelected(ann.id);
    };

    let shape = null;
    if (ann.type === 'rect') {
      const x = fromPercent(ann.data.x, renderSize.w);
      const y = fromPercent(ann.data.y, renderSize.h);
      const w = fromPercent(ann.data.w, renderSize.w);
      const h = fromPercent(ann.data.h, renderSize.h);
      shape = (
        <rect x={x} y={y} width={w} height={h} fill={color} fillOpacity={opacity}
          stroke={color} strokeWidth={strokeW} strokeDasharray={isSelected ? '6,3' : 'none'} rx={2} />
      );
    } else if (ann.type === 'circle') {
      const cx = fromPercent(ann.data.cx, renderSize.w);
      const cy = fromPercent(ann.data.cy, renderSize.h);
      const rx = fromPercent(ann.data.rx, renderSize.w);
      const ry = fromPercent(ann.data.ry, renderSize.h);
      shape = (
        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={color} fillOpacity={opacity}
          stroke={color} strokeWidth={strokeW} strokeDasharray={isSelected ? '6,3' : 'none'} />
      );
    } else if (ann.type === 'point') {
      const cx = fromPercent(ann.data.cx, renderSize.w);
      const cy = fromPercent(ann.data.cy, renderSize.h);
      shape = (
        <>
          <circle cx={cx} cy={cy} r={isSelected ? 9 : 7} fill={color} fillOpacity={0.3} stroke={color} strokeWidth={strokeW} />
          <circle cx={cx} cy={cy} r={2.5} fill={color} />
          <line x1={cx-12} y1={cy} x2={cx+12} y2={cy} stroke={color} strokeWidth={1.5} opacity={0.6} />
          <line x1={cx} y1={cy-12} x2={cx} y2={cy+12} stroke={color} strokeWidth={1.5} opacity={0.6} />
        </>
      );
    } else if (ann.type === 'polygon') {
      const pts = ann.data.points.map(p => `${fromPercent(p.px, renderSize.w)},${fromPercent(p.py, renderSize.h)}`).join(' ');
      const first = ann.data.points[0];
      shape = (
        <polygon points={pts} fill={color} fillOpacity={opacity}
          stroke={color} strokeWidth={strokeW} strokeDasharray={isSelected ? '6,3' : 'none'} />
      );
    }

    return (
      <g key={ann.id} onMouseEnter={onEnter} onMouseMove={onMove} onMouseLeave={onLeave} onClick={onClick}
        style={{ cursor: tool === 'select' ? 'pointer' : 'default' }}>
        {shape}
        {isSelected && renderHandles(ann)}
      </g>
    );
  };

  // Preview while drawing
  const renderPreview = () => {
    if (!drawing) return null;
    const { startX, startY, currentX, currentY } = drawing;
    const color = COLORS[colorIdx % COLORS.length];
    if (tool === 'rect') {
      return <rect x={Math.min(startX, currentX)} y={Math.min(startY, currentY)}
        width={Math.abs(currentX - startX)} height={Math.abs(currentY - startY)}
        fill={color} fillOpacity={0.12} stroke={color} strokeWidth={1.5} strokeDasharray="5,3" rx={2} />;
    }
    if (tool === 'circle') {
      return <ellipse cx={(startX+currentX)/2} cy={(startY+currentY)/2}
        rx={Math.abs(currentX-startX)/2} ry={Math.abs(currentY-startY)/2}
        fill={color} fillOpacity={0.12} stroke={color} strokeWidth={1.5} strokeDasharray="5,3" />;
    }
    return null;
  };

  const renderPolygonInProgress = () => {
    if (tool !== 'polygon' || polygonPoints.length === 0) return null;
    const color = COLORS[colorIdx % COLORS.length];
    const pts = polygonPoints.map(p => `${fromPercent(p.px, renderSize.w)},${fromPercent(p.py, renderSize.h)}`).join(' ');
    return (
      <g>
        <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="5,3" />
        {polygonPoints.map((p, i) => (
          <circle key={i} cx={fromPercent(p.px, renderSize.w)} cy={fromPercent(p.py, renderSize.h)} r={4} fill={color} opacity={0.8} />
        ))}
      </g>
    );
  };

  const selectedAnn = annotations.find(a => a.id === selected);
  const imgSrc = `/uploads/${image.filename}`;
  const pct = Math.round(zoom * 100);
  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;

  // Reset polygon edit mode and vertex hover when selection changes
  useEffect(() => { setPolyMode(null); setHoveredVertex(null); }, [selected]);

  const handleSVGDoubleClick = useCallback((e) => {
    const curSel = annotations.find(a => a.id === selected);
    if (!curSel || curSel.type !== 'polygon' || polyMode) return;
    e.preventDefault();
    const { x, y } = getSVGCoords(e);
    const pts = curSel.data.points;
    let minDist = Infinity, insertIdx = 0;
    for (let j = 0; j < pts.length; j++) {
      const nxt = (j + 1) % pts.length;
      const d = distToSegment(x, y,
        fromPercent(pts[j].px, renderSize.w), fromPercent(pts[j].py, renderSize.h),
        fromPercent(pts[nxt].px, renderSize.w), fromPercent(pts[nxt].py, renderSize.h));
      if (d < minDist) { minDist = d; insertIdx = j; }
    }
    insertPolygonPoint(curSel.id, insertIdx, toPercent(x, renderSize.w), toPercent(y, renderSize.h));
  }, [annotations, selected, polyMode, getSVGCoords, renderSize, insertPolygonPoint]);

  // When polyMode activates, switch to select tool
  useEffect(() => { if (polyMode) setTool('select'); }, [polyMode]);

  const changeTool = (toolName) => {
    setTool(toolName);
    setPolygonPoints([]);
    setSelected(null);
    setPolyMode(null);
  };

  const TOOL_TITLES = {
    select: t('editor.tool_select_title'),
    rect: t('editor.tool_rect_title'),
    circle: t('editor.tool_circle_title'),
    polygon: t('editor.tool_polygon_title'),
    point: t('editor.tool_point_title'),
  };

  const TOOL_LABELS = {
    select: t('editor.tool_select'),
    rect: t('editor.tool_rect'),
    circle: t('editor.tool_circle'),
    polygon: t('editor.tool_polygon'),
    point: t('editor.tool_point'),
  };

  return (
    <div className="editor-layout">
      {/* Toolbar */}
      <div className="editor-toolbar">
        <div className="editor-toolbar-title">
          <span className="editor-image-name">{image.name}</span>
          <span className="badge badge-accent">{image.original_width}×{image.original_height}px</span>
        </div>
        <div className="editor-tools">
          {Object.entries(TOOL_ICONS).map(([toolName, icon]) => (
            <button key={toolName} className={`tool-btn ${tool === toolName ? 'active' : ''}`}
              onClick={() => changeTool(toolName)}
              title={TOOL_TITLES[toolName]}>
              <span>{icon}</span>
              <span className="tool-label">{TOOL_LABELS[toolName]}</span>
            </button>
          ))}
        </div>
        <div className="editor-toolbar-actions">
          {/* History controls */}
          <div className="zoom-controls">
            <button className="zoom-btn" onClick={undo} disabled={!canUndo} title={t('editor.undo_title')}>↩</button>
            <button className="zoom-btn" onClick={redo} disabled={!canRedo} title={t('editor.redo_title')}>↪</button>
          </div>
          {/* Zoom controls */}
          <div className="zoom-controls">
            <button className="btn-icon zoom-btn" onClick={() => setZoom(z => clamp(parseFloat((z - ZOOM_STEP).toFixed(3)), ZOOM_MIN, ZOOM_MAX))} title={t('editor.zoom_out_title')}>−</button>
            <span className="zoom-label">{pct}%</span>
            <button className="btn-icon zoom-btn" onClick={() => setZoom(z => clamp(parseFloat((z + ZOOM_STEP).toFixed(3)), ZOOM_MIN, ZOOM_MAX))} title={t('editor.zoom_in_title')}>+</button>
            <button className="btn-icon zoom-btn" onClick={() => setZoomReady(false)} title={t('editor.fit_screen_title')}>⊡</button>
          </div>
          {tool === 'polygon' && polygonPoints.length >= 3 && (
            <button className="btn btn-success btn-sm" title={t('editor.close_polygon_title')} onClick={finishPolygon}>{t('editor.close_polygon')}</button>
          )}
          {tool === 'polygon' && polygonPoints.length > 0 && (
            <button className="btn btn-warning btn-sm" title={t('editor.discard_polygon_title')} onClick={() => setPolygonPoints([])}>{t('editor.discard_polygon')}</button>
          )}
          <button className={`btn btn-sm ${saved ? 'btn-success' : 'btn-primary'}`} title={t('editor.save_title')} onClick={handleSave}>
            {saved ? t('editor.saved') : t('editor.save')}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportAnnotationsJSON({ ...image, annotations })} title={t('editor.export_json_title')}>
            {t('editor.export_json')}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => window.open(`/public/images/${image.id}.svg`, '_blank')} title={t('editor.export_svg_title')}>
            {t('editor.export_svg')}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={toggleTheme} title={t('editor.toggle_theme_title')}>◑</button>
          <button className="btn btn-secondary btn-sm" onClick={onClose} title={t('editor.close_title')}>{t('editor.close')}</button>
        </div>
      </div>

      {selected && (
        <div className="poly-mode-bar">
          <button className="btn btn-danger btn-sm poly-mode-exit"
            title={t('editor.end_edit_title')}
            onClick={() => { setSelected(null); setPolyMode(null); }}>
            {t('editor.end_edit')}
          </button>
          <span className="poly-mode-label">
            {t('editor.editing')} {selectedAnn ? TYPE_LABELS[selectedAnn.type] : ''}:
          </span>
          {selectedAnn?.type === 'polygon' && (
            <>
              <button
                className={`btn btn-sm ${polyMode === 'add' ? 'btn-primary' : 'btn-secondary'}`}
                title={t('editor.add_points_title')}
                onClick={() => setPolyMode(m => m === 'add' ? null : 'add')}>
                {t('editor.add_points')}
              </button>
              <button
                className={`btn btn-sm ${polyMode === 'remove' ? 'btn-danger' : 'btn-secondary'}`}
                title={t('editor.remove_points_title')}
                onClick={() => setPolyMode(m => m === 'remove' ? null : 'remove')}>
                {t('editor.remove_points')}
              </button>
              {polyMode && (
                <span className={`poly-mode-active-label ${polyMode}`}>
                  {polyMode === 'add' ? t('editor.adding_points') : t('editor.removing_points')}
                </span>
              )}
            </>
          )}
        </div>
      )}

      <div className="editor-body">
        {/* Canvas */}
        <div className="editor-canvas-wrap" ref={wrapRef}
          style={{ cursor: isPanning ? 'grabbing' : undefined }}>
          <div className="editor-canvas-inner" ref={canvasRef}
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top left',
                     width: image.original_width, height: image.original_height }}>
            <img
              ref={imgRef}
              src={imgSrc}
              alt={image.name}
              className="editor-image"
              style={{ width: image.original_width, height: image.original_height, display: 'block' }}
              draggable={false}
            />
            <svg
              ref={svgRef}
              className="editor-svg"
              style={{
                cursor: polyMode === 'add' ? 'crosshair'
                  : polyMode === 'remove' ? 'default'
                  : (selectedAnn?.type === 'polygon' && !polyMode) ? 'crosshair'
                  : tool === 'select' ? 'default'
                  : 'crosshair',
                width: image.original_width, height: image.original_height,
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onClick={handleSVGClick}
              onDoubleClick={handleSVGDoubleClick}
            >
              {annotations.map(ann => renderAnnotation(ann, selected === ann.id))}
              {renderPreview()}
              {renderPolygonInProgress()}

            </svg>
          </div>
        </div>

        {/* Sidebar */}
        <div className="editor-sidebar">
          {/* Selected annotation panel */}
          {selectedAnn && (
            <div className="sidebar-section selected-panel">
              <div className="sidebar-header">
                <span>{t('editor.selected_ann')}</span>
                <button className="btn-icon btn-sm" onClick={() => setSelected(null)} title={t('editor.deselect_title')}>✕</button>
              </div>
              <div className="selected-ann-info">
                <div className="ann-dot-big" style={{ background: selectedAnn.color || COLORS[0] }} />
                <div>
                  <div className="selected-label">{selectedAnn.title || selectedAnn.label}</div>
                </div>
              </div>
              <div className="selected-type-row">
                <span className="field-micro-label">{t('editor.type_label')}</span>
                <div className="type-switcher">
                  {Object.entries(TYPE_LABELS).map(([typeName, lbl]) => (
                    <button key={typeName}
                      className={`type-btn ${selectedAnn.type === typeName ? 'active' : ''}`}
                      title={lbl}
                      onClick={() => {
                        // change type: reset data to a default for the new type, keep code/label/color
                        const d = selectedAnn.data;
                        let newData = {};
                        if (typeName === 'rect') newData = { x: d.x ?? d.cx ?? 20, y: d.y ?? d.cy ?? 20, w: d.w ?? (d.rx ? d.rx*2 : 20), h: d.h ?? (d.ry ? d.ry*2 : 20) };
                        else if (typeName === 'circle') newData = { cx: d.cx ?? (d.x ? d.x+d.w/2 : 30), cy: d.cy ?? (d.y ? d.y+d.h/2 : 30), rx: d.rx ?? (d.w ? d.w/2 : 15), ry: d.ry ?? (d.h ? d.h/2 : 15) };
                        else if (typeName === 'point') newData = { cx: d.cx ?? (d.x ? d.x+d.w/2 : 50), cy: d.cy ?? (d.y ? d.y+d.h/2 : 50) };
                        else if (typeName === 'polygon') {
                          const cx = d.cx ?? (d.x ? d.x+d.w/2 : 50);
                          const cy = d.cy ?? (d.y ? d.y+d.h/2 : 50);
                          const r = 15;
                          newData = { points: [
                            { px: cx, py: cy - r }, { px: cx + r, py: cy + r }, { px: cx - r, py: cy + r }
                          ]};
                        }
                        pushHistory(annotations);
                        setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? { ...a, type: typeName, data: newData } : a));
                      }}>
                      {TOOL_ICONS[typeName]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Numeric edit of coords */}
              <div className="coords-editor">
                <button className="coords-toggle" onClick={() => setShowCoords(v => !v)}>
                  <span className="field-micro-label">{t('editor.coords_label')}</span>
                  <span className="coords-toggle-icon">{showCoords ? '▲' : '▼'}</span>
                </button>
                {showCoords && selectedAnn.type === 'rect' && (
                  <div className="coords-grid">
                    {[['x','X'],['y','Y'],['w','W'],['h','H']].map(([k, lbl]) => (
                      <div key={k} className="coord-field">
                        <label>{lbl}</label>
                        <input type="number" step="0.1" value={selectedAnn.data[k]?.toFixed(1) ?? 0}
                          onChange={e => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? { ...a, data: { ...a.data, [k]: parseFloat(e.target.value)||0 } } : a))} />
                      </div>
                    ))}
                  </div>
                )}
                {showCoords && selectedAnn.type === 'circle' && (
                  <div className="coords-grid">
                    {[['cx','CX'],['cy','CY'],['rx','RX'],['ry','RY']].map(([k, lbl]) => (
                      <div key={k} className="coord-field">
                        <label>{lbl}</label>
                        <input type="number" step="0.1" value={selectedAnn.data[k]?.toFixed(1) ?? 0}
                          onChange={e => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? { ...a, data: { ...a.data, [k]: parseFloat(e.target.value)||0 } } : a))} />
                      </div>
                    ))}
                  </div>
                )}
                {showCoords && selectedAnn.type === 'point' && (
                  <div className="coords-grid">
                    {[['cx','X'],['cy','Y']].map(([k, lbl]) => (
                      <div key={k} className="coord-field">
                        <label>{lbl}</label>
                        <input type="number" step="0.1" value={selectedAnn.data[k]?.toFixed(1) ?? 0}
                          onChange={e => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? { ...a, data: { ...a.data, [k]: parseFloat(e.target.value)||0 } } : a))} />
                      </div>
                    ))}
                  </div>
                )}
                {showCoords && selectedAnn.type === 'polygon' && (
                  <div className="polygon-points-list">
                    {selectedAnn.data.points.map((p, i) => (
                      <div key={i} className="poly-point-row">
                        <span className="poly-idx">P{i+1}</span>
                        <input type="number" step="0.1" value={p.px.toFixed(1)}
                          onChange={e => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? {
                            ...a, data: { points: a.data.points.map((pt, j) => j === i ? { px: parseFloat(e.target.value)||0, py: pt.py } : pt) }
                          } : a))} />
                        <input type="number" step="0.1" value={p.py.toFixed(1)}
                          onChange={e => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? {
                            ...a, data: { points: a.data.points.map((pt, j) => j === i ? { px: pt.px, py: parseFloat(e.target.value)||0 } : pt) }
                          } : a))} />
                        <button className="poly-del-btn" onClick={() => {
                          pushHistory(annotations);
                          setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? {
                            ...a, data: { points: a.data.points.filter((_,j) => j !== i) }
                          } : a));
                        }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Color picker for selected */}
              <div>
                <span className="field-micro-label">{t('editor.color_label')}</span>
                <div className="color-palette" style={{ marginTop: 6 }}>
                  {COLORS.map((c) => (
                    <button key={c} className={`color-swatch ${selectedAnn.color === c ? 'active' : ''}`}
                      style={{ background: c }}
                      onClick={() => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? { ...a, color: c } : a))} />
                  ))}
                </div>
              </div>

              {/* Link field */}
              <div>
                <span className="field-micro-label">{t('editor.link_label')}</span>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={selectedAnn.link || ''}
                    style={{ flex: 1, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 8px', color: 'var(--text)', fontSize: 12 }}
                    onChange={e => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? { ...a, link: e.target.value || null } : a))}
                  />
                  {selectedAnn.link && (
                    <a href={selectedAnn.link} target="_blank" rel="noopener noreferrer"
                      className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}>↗</a>
                  )}
                </div>
                {selectedAnn.link && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                    <LinkColorSelect t={t} value={selectedAnn.linkColor || ''} style={{ flex: 1 }}
                      onChange={v => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? { ...a, linkColor: v || null } : a))} />
                    <IconField value={selectedAnn.linkIcon || ''}
                      onChange={v => setAnnotations(prev => prev.map(a => a.id === selectedAnn.id ? { ...a, linkIcon: v || null } : a))} />
                  </div>
                )}
              </div>

              <div className="selected-actions">
                <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => setEditingMeta(selectedAnn)}>{t('editor.edit_details')}</button>
                <button className="btn btn-danger btn-sm" title={t('editor.delete_ann_title')} onClick={() => deleteAnnotation(selectedAnn.id)}>{t('editor.delete_annotation')}</button>
              </div>
            </div>
          )}

          <div className="sidebar-section">
            <div className="sidebar-header">
              <span>{t('editor.annotations')}</span>
              <span className="badge badge-accent">{annotations.length}</span>
            </div>
            <div className="annotations-list" ref={annListRef}>
              {annotations.length === 0 && (
                <div className="empty-ann">{t('editor.no_annotations')}</div>
              )}
              {annotations.map(ann => (
                <div key={ann.id} data-ann-id={ann.id}
                  className={`annotation-item${selected === ann.id ? ' selected' : ''}${hoveredShape === ann.id ? ' hovered' : ''}`}
                  onClick={() => { setSelected(ann.id === selected ? null : ann.id); setTool('select'); }}>
                  <div className="ann-thumb">
                    <div className="ann-dot" style={{ background: ann.color || COLORS[0] }} />
                    <img
                      src={`/public/images/${image.id}/annotations/${ann.id}.png${ann.last_change_at ? `?_=${Math.floor(new Date(ann.last_change_at + 'Z').getTime() / 1000)}` : ''}`}
                      alt=""
                      className="ann-thumb-img"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="ann-info">
                    <div className="ann-label">{ann.title || ann.label}</div>
                    <div className="ann-type">{TOOL_ICONS[ann.type]} {t('type.' + ann.type)}</div>
                  </div>
                  <div className="ann-actions">
                    <button className="btn-icon btn-sm" onClick={e => { e.stopPropagation(); setEditingMeta(ann); }} title={t('editor.edit_details')}>✎</button>
                    <button className="btn-icon btn-sm" style={{ color: 'var(--danger)' }}
                      onClick={e => { e.stopPropagation(); deleteAnnotation(ann.id); }} title={t('editor.delete_ann_title')}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!selectedAnn && (
            <div className="sidebar-section">
              <div className="sidebar-header">{t('editor.color_label')}</div>
              <div className="color-palette">
                {COLORS.map((c, i) => (
                  <button key={c} className={`color-swatch ${colorIdx % COLORS.length === i ? 'active' : ''}`}
                    style={{ background: c }} onClick={() => setColorIdx(i)} />
                ))}
              </div>
            </div>
          )}

          {allTags.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-header">{t('editor.tags_label')}</div>
              <div className="tag-picker">
                {allTags.map(tag => (
                  <button
                    key={tag.id}
                    className={`tag-pill tag-pill-sm ${imageTags.includes(tag.id) ? 'active' : ''}`}
                    style={{ '--tag-color': tag.color }}
                    onClick={() => setImageTags(prev =>
                      prev.includes(tag.id) ? prev.filter(x => x !== tag.id) : [...prev, tag.id]
                    )}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="sidebar-section sidebar-help">
            <div className="sidebar-header">{t('editor.tips')}</div>
            <div className="help-text">
              <p>{t('editor.help_scroll')}</p>
              <p>{t('editor.help_middle_mouse')}</p>
              <p>{t('editor.help_ctrl_zoom')}</p>
              <p>{t('editor.help_undo_redo')}</p>
              <p>{t('editor.help_select_edit')}</p>
              <p>{t('editor.help_polygon_add')}</p>
              <p>{t('editor.help_polygon_remove')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip rendered outside the scaled container so position:fixed works correctly */}
      {tooltip && !handleDrag && !selected && (() => {
        const ann = annotations.find(a => a.id === tooltip.id);
        if (!ann) return null;
        const color = ann.color || COLORS[0];
        const slides = annSlides(ann, image.id);
        return (
          <div className="ann-html-tooltip" style={{
            left: tooltip.screenX + 14, top: tooltip.screenY - 64,
            borderColor: color, padding: 0, overflow: 'hidden', minWidth: 160,
          }}
            onMouseEnter={() => clearTimeout(ttHideTimer.current)}
            onMouseLeave={() => { ttHideTimer.current = setTimeout(() => setTooltip(null), 200); }}
          >
            {slides.length > 0 && <Slideshow slides={slides} maxHeight={110} />}
            <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span className="tt-label">{ann.title || ann.label}</span>
              {ann.link && (
                <a className="tt-link" href={ann.link} target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}>
                  {t('common.open_link')}
                </a>
              )}
            </div>
          </div>
        );
      })()}

      {pendingAnnotation && (
        <AnnotationDialog
          onConfirm={confirmAnnotation}
          onCancel={() => { setPendingAnnotation(null); setPolygonPoints([]); }}
        />
      )}

      {editingMeta && (
        <AnnotationDialog
          ann={editingMeta}
          initialCode={editingMeta.code}
          initialTitle={editingMeta.title || editingMeta.label || ''}
          initialContent={editingMeta.content || ''}
          initialLink={editingMeta.link || ''}
          initialLinkColor={editingMeta.linkColor || ''}
          initialLinkIcon={editingMeta.linkIcon || ''}
          initialLastChangeAt={editingMeta.last_change_at || ''}
          onConfirm={(code, title, content, link, updatedImages, linkColor, linkIcon) => {
            setAnnotations(a => a.map(x => x.id === editingMeta.id
              ? { ...x, code, title, content: content || '', link: link || null, linkColor: linkColor || null, linkIcon: linkIcon || null, images: updatedImages ?? x.images }
              : x));
            setEditingMeta(null);
          }}
          onCancel={() => setEditingMeta(null)}
          editMode
        />
      )}

      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.screenX} y={ctxMenu.screenY}
          items={ctxMenu.type === 'edge'
            ? [{ label: t('editor.ctx_add_point'), onClick: () => insertPolygonPoint(ctxMenu.annId, ctxMenu.edgeIdx, ctxMenu.insertPx, ctxMenu.insertPy) }]
            : [{ label: t('editor.ctx_delete_point'), danger: true, onClick: () => deletePolygonPoint(ctxMenu.annId, ctxMenu.vertexIdx) }]
          }
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}

function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const el = ref.current;
    if (x + rect.width > window.innerWidth) el.style.left = `${x - rect.width}px`;
    if (y + rect.height > window.innerHeight) el.style.top = `${y - rect.height}px`;
  }, [x, y]);
  return (
    <div ref={ref} className="ctx-menu" style={{ left: x, top: y }}
      onClick={e => e.stopPropagation()} onContextMenu={e => { e.preventDefault(); e.stopPropagation(); }}>
      {items.map((item, i) => (
        <button key={i} className={`ctx-menu-item${item.danger ? ' danger' : ''}`}
          onClick={() => { item.onClick(); onClose(); }}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

function AnnotationImagesPanel({ ann, images, onImagesChange }) {
  const { t } = useI18n();
  const [tab, setTab] = useState('url');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [storeOnDisk, setStoreOnDisk] = useState(false);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [editingImg, setEditingImg] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editLink, setEditLink] = useState('');
  const [editLinkText, setEditLinkText] = useState('');
  const [editLinks, setEditLinks] = useState([]);
  const fileRef = useRef(null);
  // "From page" tab: scrape images from one or more page URLs via the backend
  // import command, then let the user pick which results to add.
  const [pageUrls, setPageUrls] = useState(['']);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // [{page_url, images:[...]}]
  const [importSelected, setImportSelected] = useState(() => new Set());
  const [importStoreOnDisk, setImportStoreOnDisk] = useState(false);
  const [importAdding, setImportAdding] = useState(false);

  const reset = () => {
    setUrl(''); setTitle(''); setDescription(''); setLink('');
    setStoreOnDisk(false); setFile(null);
    setPageUrls(['']);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleAdd = async () => {
    if (tab === 'page') { handleImportFromPages(); return; }
    setSaving(true);
    try {
      let created;
      if (tab === 'file') {
        if (!file) return;
        created = await uploadAnnotationImage(ann.id, file, title, description, link);
      } else {
        if (!url.trim()) return;
        created = await createAnnotationImage(ann.id, url.trim(), title, storeOnDisk, description, link);
      }
      onImagesChange([...images, created]);
      reset();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setPageUrlAt = (idx, value) => {
    setPageUrls(prev => prev.map((u, i) => (i === idx ? value : u)));
  };
  const addPageUrlRow = () => setPageUrls(prev => [...prev, '']);
  const removePageUrlRow = (idx) => {
    setPageUrls(prev => (prev.length <= 1 ? [''] : prev.filter((_, i) => i !== idx)));
  };

  const handleImportFromPages = async () => {
    const urls = pageUrls.map(u => u.trim()).filter(Boolean);
    if (!urls.length) return;
    setImporting(true);
    try {
      const result = await importImagesFromWeb(urls, storeOnDisk);
      setImportResult(result || []);
      setImportSelected(new Set());
      setImportStoreOnDisk(storeOnDisk);
    } catch (e) {
      alert(e.message);
    } finally {
      setImporting(false);
    }
  };

  // Stable key for an imported image (page index + image index) so selection
  // survives re-renders without relying on possibly-duplicate URLs.
  const importKey = (pageIdx, imgIdx) => `${pageIdx}:${imgIdx}`;

  const toggleImportSelect = (key) => {
    setImportSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const importAllKeys = () => {
    const keys = [];
    (importResult || []).forEach((page, pi) =>
      (page.images || []).forEach((_, ii) => keys.push(importKey(pi, ii))));
    return keys;
  };

  const closeImportDialog = () => { setImportResult(null); setImportSelected(new Set()); };

  const handleAddSelectedImports = async () => {
    if (!importSelected.size) return;
    setImportAdding(true);
    const added = [];
    try {
      for (let pi = 0; pi < importResult.length; pi++) {
        const page = importResult[pi];
        const pageImages = page.images || [];
        for (let ii = 0; ii < pageImages.length; ii++) {
          if (!importSelected.has(importKey(pi, ii))) continue;
          const img = pageImages[ii];
          const created = await createAnnotationImage(
            ann.id, img.image_url, '', importStoreOnDisk, img.description || '', img.link || '');
          added.push(created);
        }
      }
      if (added.length) onImagesChange([...images, ...added]);
      closeImportDialog();
      reset();
    } catch (e) {
      alert(e.message);
      if (added.length) onImagesChange([...images, ...added]);
    } finally {
      setImportAdding(false);
    }
  };

  const handleDelete = async (imgId) => {
    try {
      await deleteAnnotationImage(ann.id, imgId);
      onImagesChange(images.filter(i => i.id !== imgId));
    } catch (e) {
      alert(e.message);
    }
  };

  const moveImage = async (idx, dir) => {
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= images.length) return;
    const a = images[idx];
    const b = images[swapIdx];
    const aOrder = a.sort_order;
    const bOrder = b.sort_order;
    const newImages = images.map((img, i) => {
      if (i === idx) return { ...img, sort_order: bOrder };
      if (i === swapIdx) return { ...img, sort_order: aOrder };
      return img;
    }).sort((x, y) => (x.sort_order ?? 0) - (y.sort_order ?? 0));
    onImagesChange(newImages);
    try {
      await Promise.all([
        updateAnnotationImage(ann.id, a.id, { sort_order: bOrder }),
        updateAnnotationImage(ann.id, b.id, { sort_order: aOrder }),
      ]);
    } catch (e) {
      alert(e.message);
      onImagesChange(images);
    }
  };

  const openEdit = (img) => {
    setEditingImg(img);
    setEditTitle(img.title || '');
    setEditDesc(img.description || '');
    setEditLink(img.link || '');
    setEditLinkText(img.linkText || '');
    setEditLinks((img.links || []).map(l => ({ ...l })));
  };
  const cancelEdit = () => setEditingImg(null);

  // Link-list editor helpers (the `links` array on an annotation image).
  const cleanLink = (l) => {
    const o = { url: (l.url || '').trim() };
    const label = (l.label || '').trim(); if (label) o.label = label;
    const icon = (l.icon || '').trim(); if (icon) o.icon = icon;
    const cls = (l.class || '').trim(); if (cls) o.class = cls;
    const color = (l.color || '').trim(); if (color) o.color = color;
    if (l.hideLabel) o.hideLabel = true;
    return o;
  };
  const setLinkAt = (idx, patch) => setEditLinks(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLink = () => setEditLinks(prev => [...prev, { url: '', label: '', icon: '', class: '', color: '', hideLabel: false }]);
  const removeLink = (idx) => setEditLinks(prev => prev.filter((_, i) => i !== idx));
  const moveLink = (idx, dir) => setEditLinks(prev => {
    const j = idx + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = prev.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const saveEdit = async () => {
    const img = editingImg;
    setEditingImg(null);
    const changed = {};
    const newTitle = editTitle.trim();
    const newLink = editLink.trim();
    const newLinkText = editLinkText.trim();
    if (newTitle !== (img.title || '')) changed.title = newTitle;
    if (editDesc !== (img.description || '')) changed.description = editDesc;
    if (newLink !== (img.link || '')) changed.link = newLink;
    if (newLinkText !== (img.linkText || '')) changed.linkText = newLinkText;
    const cleanLinks = editLinks.map(cleanLink).filter(l => l.url);
    const origLinks = (img.links || []).map(cleanLink).filter(l => l.url);
    if (JSON.stringify(cleanLinks) !== JSON.stringify(origLinks)) changed.links = cleanLinks;
    if (!Object.keys(changed).length) return;
    try {
      const updated = await updateAnnotationImage(ann.id, img.id, changed);
      onImagesChange(images.map(i => i.id === img.id ? updated : i));
    } catch (e) { alert(e.message); }
  };

  useEffect(() => {
    if (!editingImg) return;
    const onKey = e => { if (e.key === 'Escape') cancelEdit(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingImg]);

  const canAdd = tab === 'file'
    ? !!file
    : tab === 'page'
      ? pageUrls.some(u => u.trim())
      : !!url.trim();

  return (
    <div className="ann-img-panel">
      <div className="ann-img-panel-header">
        <span>{t('editor.images_label')}</span>
        {images.length > 0 && <span className="badge badge-accent">{images.length}</span>}
      </div>

      <div className="ann-img-tabs">
        <button className={`ann-img-tab${tab === 'url' ? ' active' : ''}`} onClick={() => setTab('url')}>{t('editor.tab_url')}</button>
        <button className={`ann-img-tab${tab === 'file' ? ' active' : ''}`} onClick={() => setTab('file')}>{t('editor.tab_upload')}</button>
        <button className={`ann-img-tab${tab === 'page' ? ' active' : ''}`} onClick={() => setTab('page')}>{t('editor.tab_from_page')}</button>
      </div>

      <div className="ann-img-form">
        {tab === 'page' ? (
          <>
            <label className="ann-img-check">
              <input type="checkbox" checked={storeOnDisk} onChange={e => setStoreOnDisk(e.target.checked)} />
              {t('editor.store_on_disk')}
            </label>
            <div className="ann-img-page-urls">
              {pageUrls.map((pu, idx) => (
                <div key={idx} className="ann-img-page-url-row">
                  <input
                    type="url"
                    placeholder={t('editor.page_url_placeholder')}
                    value={pu}
                    onChange={e => setPageUrlAt(idx, e.target.value)}
                    className="ann-img-input"
                    onKeyDown={e => e.key === 'Enter' && canAdd && !importing && handleAdd()}
                  />
                  <button type="button" className="btn-icon btn-sm" title={t('editor.remove_page_url')}
                    disabled={pageUrls.length === 1 && !pu} onClick={() => removePageUrlRow(idx)}>✕</button>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addPageUrlRow}>
              {t('editor.add_page_url')}
            </button>
            <button className="btn btn-primary btn-sm" disabled={!canAdd || importing} onClick={handleAdd}>
              {importing ? t('editor.importing') : t('editor.import_from_web_btn')}
            </button>
          </>
        ) : (
          <>
            {tab === 'url' ? (
              <>
                <input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  className="ann-img-input"
                  onKeyDown={e => e.key === 'Enter' && canAdd && handleAdd()}
                />
                <label className="ann-img-check">
                  <input type="checkbox" checked={storeOnDisk} onChange={e => setStoreOnDisk(e.target.checked)} />
                  {t('editor.store_on_server')}
                </label>
              </>
            ) : (
              <div className="ann-img-file-row">
                <input ref={fileRef} type="file" accept="image/*" className="ann-img-file-input"
                  onChange={e => setFile(e.target.files[0] || null)} />
              </div>
            )}
            <input type="text" placeholder={t('editor.title_optional')} value={title}
              onChange={e => setTitle(e.target.value)} className="ann-img-input" />
            <RichTextEditor value={description} onChange={setDescription} placeholder={t('editor.img_desc_placeholder')} />
            <input type="url" placeholder={t('editor.img_link_placeholder')} value={link}
              onChange={e => setLink(e.target.value)} className="ann-img-input" />
            <button className="btn btn-primary btn-sm" disabled={!canAdd || saving} onClick={handleAdd}>
              {saving ? '…' : t('editor.add_image_btn')}
            </button>
          </>
        )}
      </div>

      {images.length > 0 && (
        <div className="ann-img-list">
          {images.map((img, idx) => (
            <div key={img.id} className="ann-img-item">
              <div className="ann-img-item-row">
                <span className="ann-img-idx">{idx + 1}</span>
                <img src={img.url} srcSet={imgSrcSet(img.url, img.thumbnail)} sizes="56px"
                  alt={img.title || ''} className="ann-img-thumb"
                  onClick={() => setLightboxImg(img)} onError={e => { e.target.style.opacity = '0.3'; }} />
                <div className="ann-img-info">
                  <span className="ann-img-title">
                    {img.title || <em style={{ color: 'var(--text3)', fontWeight: 400 }}>{t('editor.no_title')}</em>}
                  </span>
                  {img.description && (
                    <div className="ann-img-desc-preview" dangerouslySetInnerHTML={{ __html: img.description }} />
                  )}
                  {img.link && (
                    <a href={img.link} target="_blank" rel="noopener noreferrer" className="ann-img-link"
                      onClick={e => e.stopPropagation()}>↗ {img.link}</a>
                  )}
                </div>
                <div className="ann-img-order">
                  <button className="ann-img-order-btn" title={t('common.move_up')} disabled={idx === 0} onClick={() => moveImage(idx, -1)}>▲</button>
                  <button className="ann-img-order-btn" title={t('common.move_down')} disabled={idx === images.length - 1} onClick={() => moveImage(idx, 1)}>▼</button>
                </div>
                <button className="btn-icon btn-sm" title={t('editor.edit_details')} onClick={() => openEdit(img)}>✎</button>
                <button className="btn-icon btn-sm" style={{ color: 'var(--danger)', flexShrink: 0 }} onClick={() => handleDelete(img.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {importResult && ReactDOM.createPortal(
        <div className="ann-img-edit-overlay" onClick={importAdding ? undefined : closeImportDialog}>
          <div className="ann-img-import-dialog" onClick={e => e.stopPropagation()}>
            <div className="ann-img-edit-header">
              <span>{t('editor.import_dialog_title')}</span>
              <button className="btn-icon" onClick={closeImportDialog} disabled={importAdding}>✕</button>
            </div>
            <div className="ann-img-import-toolbar">
              <button className="btn btn-secondary btn-sm" onClick={() => setImportSelected(new Set(importAllKeys()))}>
                {t('editor.import_select_all')}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setImportSelected(new Set())}>
                {t('editor.import_clear')}
              </button>
              <span className="ann-img-import-count">{importSelected.size} {t('editor.import_selected')}</span>
            </div>
            <div className="ann-img-import-body">
              {importAllKeys().length === 0 && (
                <div className="ann-img-import-empty">{t('editor.import_no_images')}</div>
              )}
              {importResult.map((page, pi) => (
                (page.images && page.images.length > 0) && (
                  <div key={pi} className="ann-img-import-group">
                    <div className="ann-img-import-group-header">
                      <a href={page.page_url} target="_blank" rel="noopener noreferrer">{page.page_url}</a>
                      <span className="badge badge-accent">{page.images.length}</span>
                    </div>
                    <div className="ann-img-import-grid">
                      {page.images.map((img, ii) => {
                        const key = importKey(pi, ii);
                        const selected = importSelected.has(key);
                        return (
                          <div key={ii}
                            className={`ann-img-import-card${selected ? ' selected' : ''}`}
                            onClick={() => toggleImportSelect(key)}>
                            <div className="ann-img-import-check">
                              <input type="checkbox" checked={selected} readOnly tabIndex={-1} />
                            </div>
                            <img src={img.image_url} alt={img.description || ''}
                              className="ann-img-import-thumb"
                              onError={e => { e.target.style.opacity = '0.3'; }} />
                            <div className="ann-img-import-info">
                              {img.description && (
                                <div className="ann-img-import-desc" dangerouslySetInnerHTML={{ __html: img.description }} />
                              )}
                              {img.link && (
                                <a href={img.link} target="_blank" rel="noopener noreferrer"
                                  className="ann-img-link" onClick={e => e.stopPropagation()}>↗ {img.link}</a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              ))}
            </div>
            <div className="ann-img-edit-footer">
              <button className="btn btn-primary btn-sm" disabled={!importSelected.size || importAdding}
                onClick={handleAddSelectedImports}>
                {importAdding ? t('editor.import_adding') : t('editor.import_add_selected')}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={closeImportDialog} disabled={importAdding}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {editingImg && ReactDOM.createPortal(
        <div className="ann-img-edit-overlay" onClick={cancelEdit}>
          <div className="ann-img-edit-dialog" onClick={e => e.stopPropagation()}>
            <div className="ann-img-edit-header">
              <span>{t('editor.edit_details')}</span>
              <button className="btn-icon" onClick={cancelEdit}>✕</button>
            </div>
            <div className="ann-img-edit-body">
              <label className="field-micro-label">{t('editor.title_optional')}</label>
              <input autoFocus type="text" className="ann-img-input" value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); }} />
              <label className="field-micro-label">{t('editor.img_desc_placeholder')}</label>
              <RichTextEditor value={editDesc} onChange={setEditDesc} />
              <label className="field-micro-label">{t('editor.img_link_placeholder')}</label>
              <input type="url" className="ann-img-input" value={editLink}
                onChange={e => setEditLink(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); }} />
              {editLink.trim() && (
                <>
                  <label className="field-micro-label">{t('editor.img_link_text_placeholder')}</label>
                  <input type="text" className="ann-img-input" value={editLinkText}
                    onChange={e => setEditLinkText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); }} />
                </>
              )}

              <label className="field-micro-label">{t('editor.links_label')}</label>
              <div className="ann-img-links-edit">
                {editLinks.map((lk, idx) => (
                  <div key={idx} className="ann-img-link-row">
                    <div className="ann-img-link-row-main">
                      <input className="ann-img-input" type="url" placeholder={t('editor.link_url_placeholder')}
                        value={lk.url || ''} onChange={e => setLinkAt(idx, { url: e.target.value })} />
                      <div className="ann-img-link-reorder">
                        <button type="button" className="btn-icon btn-sm" title={t('editor.move_link_up')}
                          disabled={idx === 0} onClick={() => moveLink(idx, -1)}>↑</button>
                        <button type="button" className="btn-icon btn-sm" title={t('editor.move_link_down')}
                          disabled={idx === editLinks.length - 1} onClick={() => moveLink(idx, 1)}>↓</button>
                        <button type="button" className="btn-icon btn-sm" title={t('editor.remove_link')}
                          onClick={() => removeLink(idx)}>✕</button>
                      </div>
                    </div>
                    <div className="ann-img-link-row-opts">
                      <input className="ann-img-input" type="text" placeholder={t('editor.link_label_placeholder')}
                        value={lk.label || ''} onChange={e => setLinkAt(idx, { label: e.target.value })} />
                      <IconField value={lk.icon || ''} onChange={v => setLinkAt(idx, { icon: v })} />
                      <input className="ann-img-input" type="text" placeholder={t('editor.link_class_placeholder')}
                        value={lk.class || ''} onChange={e => setLinkAt(idx, { class: e.target.value })} />
                      <LinkColorSelect t={t} value={lk.color || ''}
                        onChange={v => setLinkAt(idx, { color: v })} />
                    </div>
                    <label className="ann-img-check">
                      <input type="checkbox" checked={!!lk.hideLabel}
                        onChange={e => setLinkAt(idx, { hideLabel: e.target.checked })} />
                      {t('editor.link_hide_label')}
                    </label>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={addLink}>
                  {t('editor.add_link')}
                </button>
              </div>
            </div>
            <div className="ann-img-edit-footer">
              <button className="btn btn-primary btn-sm" onClick={saveEdit}>{t('common.save')}</button>
              <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {lightboxImg && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setLightboxImg(null)}>
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', background: '#111', borderRadius: 10, overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,.8)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(0,0,0,0.6)', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
              <span style={{ fontSize: 13, color: '#9090b0' }}>{lightboxImg.title || t('editor.image_label')}</span>
              <button className="btn-icon" style={{ color: '#ccc' }} onClick={() => setLightboxImg(null)}>✕</button>
            </div>
            <div style={{ position: 'relative' }}>
              <img src={lightboxImg.url} srcSet={imgSrcSet(lightboxImg.url, lightboxImg.thumbnail)} sizes="90vw"
                alt={lightboxImg.title || ''}
                style={{ display: 'block', maxWidth: '90vw', maxHeight: 'calc(90vh - 50px)', objectFit: 'contain' }} />
              {(lightboxImg.title || lightboxImg.description || lightboxImg.link) && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px 16px 14px', background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }}>
                  {lightboxImg.title && <div style={{ fontSize: 13, fontWeight: 600, color: '#e8e8ff', marginBottom: 4 }}>{lightboxImg.title}</div>}
                  {lightboxImg.description && <div style={{ fontSize: 12, color: '#b0b0cc', marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: lightboxImg.description }} />}
                  {lightboxImg.link && <a href={lightboxImg.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#5b5ef4', border: '1px solid rgba(91,94,244,0.5)', borderRadius: 4, padding: '2px 8px', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>{t('common.open_link')}</a>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnnotationDialog({
  ann,
  onConfirm, onCancel,
  initialCode = '', initialTitle = '', initialContent = '', initialLink = '', initialLinkColor = '', initialLinkIcon = '',
  initialLastChangeAt = '',
  editMode = false,
}) {
  const { t } = useI18n();
  const [isMax, setIsMax] = useState(false);
  const [code, setCode]         = useState(initialCode || `ANN-${Math.floor(Math.random() * 9000) + 1000}`);
  const [title, setTitle]       = useState(initialTitle);
  const [content, setContent]   = useState(initialContent);
  const [link, setLink]         = useState(initialLink);
  const [linkColor, setLinkColor] = useState(initialLinkColor);
  const [linkIcon, setLinkIcon] = useState(initialLinkIcon);
  const [images, setImages]     = useState(ann?.images || []);

  return (
    <div className={`modal-overlay${isMax ? ' maximized' : ''}`}>
      <div className={`modal${isMax ? ' maximized' : ''}`} style={isMax ? undefined : { maxWidth: 540 }}>
        <div className="modal-header">
          <h2>{editMode ? t('editor.edit_ann_title') : t('editor.confirm_title')}</h2>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-icon" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn-icon" title={t('editor.cancel_btn_title')} onClick={onCancel}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>{t('editor.code_label')}</label>
            <input value={code} onChange={e => setCode(e.target.value)} placeholder={t('editor.code_placeholder')} />
          </div>
          <div className="field">
            <label>{t('editor.title_label')}</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('editor.ann_title_placeholder')} autoFocus />
          </div>
          <div className="field">
            <label>{t('editor.content_label')}</label>
            <RichTextEditor value={content} onChange={setContent} />
          </div>
          <div className="field">
            <label>{t('editor.link_label')}</label>
            <input type="url" value={link} onChange={e => setLink(e.target.value)} placeholder="https://..." />
            {link.trim() && (
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                <LinkColorSelect t={t} value={linkColor} onChange={setLinkColor} style={{ flex: 1 }} />
                <IconField value={linkIcon} onChange={setLinkIcon} />
              </div>
            )}
          </div>
          {editMode && initialLastChangeAt && (
            <div className="field">
              <label>{t('editor.last_change_at')}</label>
              <input readOnly value={initialLastChangeAt} style={{ opacity: 0.55, cursor: 'default' }} />
            </div>
          )}
          {editMode && ann?.id && (
            <AnnotationImagesPanel ann={ann} images={images} onImagesChange={setImages} />
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" title={t('editor.cancel_btn_title')} onClick={onCancel}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!code || !title}
            title={t('editor.confirm_btn_title')}
            onClick={() => onConfirm(code, title, content, link || null, images, (link && linkColor) || null, (link && linkIcon) || null)}>
            {editMode ? t('common.save') : t('editor.confirm_btn')}
          </button>
        </div>
      </div>
    </div>
  );
}
